import { Button } from 'common/ui/button';
import { LabelTooltip } from 'common/ui/label-tooltip';
import { ChevronDown } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { cn } from 'utils/cn';

export interface StatusOption {
  key: string;
  color: string;
  description?: string;
  statusLabel: string;
  appStatusLabel: string;
  status: number;
}

interface StatusPickerProps {
  options: StatusOption[];
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const StatusPicker: React.FC<StatusPickerProps> = ({
  options = [],
  value,
  onChange,
  placeholder = 'Chọn trạng thái...',
  disabled = false,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find(option => option.key === value);

  const handleSelect = (statusKey: string) => {
    onChange?.(statusKey);
    setIsOpen(false);
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Auto-scroll to selected item when dropdown opens
  useEffect(() => {
    if (isOpen && value) {
      const selectedElement = document.querySelector(
        `[data-status-key="${value}"]`
      );
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }
  }, [isOpen, value]);

  const getColorClass = (color: string) => {
    switch (color) {
      case 'blue':
        return 'bg-blue-500';
      case 'yellow':
        return 'bg-yellow-400';
      case 'green':
        return 'bg-green-500';
      case 'orange':
        return 'bg-orange-500';
      case 'cyan':
        return 'bg-cyan-500';
      case 'red':
        return 'bg-red-500';
      default:
        return 'bg-gray-400';
    }
  };

  return (
    <div className={cn('relative', className)} ref={containerRef}>
      <Button
        type="button"
        variant="outline"
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className="w-full justify-between"
      >
        {selectedOption ? (
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'size-2 rounded-full',
                getColorClass(selectedOption.color)
              )}
            />
            <span className="font-medium">{selectedOption.statusLabel}</span>
          </div>
        ) : (
          <span className="text-muted-foreground">{placeholder}</span>
        )}
        <ChevronDown
          className={cn('h-4 w-4 transition-transform', isOpen && 'rotate-180')}
        />
      </Button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-y-auto">
          {options.map(option => (
            <div
              key={option.key}
              data-status-key={option.key}
              onClick={() => handleSelect(option.key)}
              className={cn(
                'p-3 cursor-pointer hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0',
                value === option.key && 'bg-lime-50 border-2 border-lime-400'
              )}
            >
              <div className="flex items-start gap-2">
                <span
                  className={cn(
                    'size-2 rounded-full mt-1.5 flex-shrink-0',
                    getColorClass(option.color)
                  )}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className={cn(
                        'font-semibold text-sm',
                        value === option.key ? 'text-lime-700' : 'text-gray-800'
                      )}
                    >
                      {option.statusLabel}
                    </span>
                    {value === option.key && (
                      <span className="text-xs bg-lime-200 text-lime-800 px-2 py-1 rounded-full">
                        Đang chọn
                      </span>
                    )}
                  </div>
               
                      <LabelTooltip 
                       label={option.appStatusLabel}
                       tooltipContent ='Trạng thái hiển thị trên app'
                       className="font-medium text-gray-600 mb-1 w-fit"/>
                    
                    <LabelTooltip 
                       label={option.key}
                       tooltipContent ='Mã lệnh'
                       className="font-medium text-gray-600 mb-1 w-fit"/>


                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StatusPicker;
